import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface FilterBarProps {
  filters: {
    make: string[];
    bodyType: string[];
    minPrice: number;
    maxPrice: number;
    minYear: number;
    maxYear: number;
    minMileage: number;
    maxMileage: number;
  };
  onFilterChange: (filters: FilterBarProps['filters']) => void;
  vehicleCount: number;
  isMobile?: boolean;
}

const MAKES = ['Toyota', 'Honda', 'Ford', 'Chevrolet', 'Nissan', 'Subaru', 'Mazda', 'Hyundai', 'Kia', 'Jeep'];
const BODY_TYPES = ['SUV', 'Truck', 'Sedan', 'Coupe', 'Hatchback', 'Wagon', 'Van', 'Convertible'];
const BUDGET_CHIPS = [
  { label: 'Under $10k', min: 0, max: 10000 },
  { label: '$10k - $15k', min: 10000, max: 15000 },
  { label: '$15k - $20k', min: 15000, max: 20000 },
  { label: '$20k - $30k', min: 20000, max: 30000 },
  { label: '$30k+', min: 30000, max: 999999 },
];

export default function FilterBar({ filters, onFilterChange, vehicleCount, isMobile = false }: FilterBarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);

  const activeFilterCount = 
    filters.make.length +
    filters.bodyType.length +
    (filters.minPrice > 0 || filters.maxPrice < 999999 ? 1 : 0) +
    (filters.minYear > 1990 || filters.maxYear < new Date().getFullYear() ? 1 : 0) +
    (filters.minMileage > 0 || filters.maxMileage < 999999 ? 1 : 0);

  const handleClearAll = () => {
    onFilterChange({
      make: [],
      bodyType: [],
      minPrice: 0,
      maxPrice: 999999,
      minYear: 1990,
      maxYear: new Date().getFullYear(),
      minMileage: 0,
      maxMileage: 999999,
    });
  };

  const toggleMake = (make: string) => {
    const newMakes = filters.make.includes(make)
      ? filters.make.filter(m => m !== make)
      : [...filters.make, make];
    onFilterChange({ ...filters, make: newMakes });
  };

  const toggleBodyType = (type: string) => {
    const newTypes = filters.bodyType.includes(type)
      ? filters.bodyType.filter(t => t !== type)
      : [...filters.bodyType, type];
    onFilterChange({ ...filters, bodyType: newTypes });
  };

  const setBudget = (min: number, max: number) => {
    onFilterChange({ ...filters, minPrice: min, maxPrice: max });
  };

  // Focus trap and escape key for mobile drawer
  useEffect(() => {
    if (!isMobile || !isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    const handleFocusTrap = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !drawerRef.current) return;
      
      const focusableElements = drawerRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('keydown', handleFocusTrap);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('keydown', handleFocusTrap);
      document.body.style.overflow = '';
    };
  }, [isMobile, isOpen]);

  const filterContent = (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1A2332] to-[#2A3442] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <i className="ri-filter-3-line text-xl text-white"></i>
          <h3 className="text-lg font-semibold text-white">Filters</h3>
          {activeFilterCount > 0 && (
            <span className="bg-[#F5C842] text-[#1A1208] text-xs font-bold px-2 py-1 rounded-full">
              {activeFilterCount}
            </span>
          )}
        </div>
        {activeFilterCount > 0 && (
          <button
            onClick={handleClearAll}
            className="text-sm text-white/80 hover:text-white transition-colors"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Scrollable Body */}
      <div className="flex-1 overflow-y-auto bg-[#FAFAF8] px-6 py-6 space-y-6">
        {/* Make */}
        <div>
          <h4 className="text-sm font-semibold text-[#1A2332] mb-3">Make</h4>
          <div className="space-y-2">
            {MAKES.map(make => (
              <label key={make} className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={filters.make.includes(make)}
                  onChange={() => toggleMake(make)}
                  className="w-4 h-4 rounded border-gray-300 text-[#F5C842] focus:ring-[#F5C842] focus:ring-offset-0"
                />
                <span className="text-sm text-[#2A3442] group-hover:text-[#1A2332] transition-colors">
                  {make}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Body Type */}
        <div>
          <h4 className="text-sm font-semibold text-[#1A2332] mb-3">Body Type</h4>
          <div className="space-y-2">
            {BODY_TYPES.map(type => (
              <label key={type} className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={filters.bodyType.includes(type)}
                  onChange={() => toggleBodyType(type)}
                  className="w-4 h-4 rounded border-gray-300 text-[#F5C842] focus:ring-[#F5C842] focus:ring-offset-0"
                />
                <span className="text-sm text-[#2A3442] group-hover:text-[#1A2332] transition-colors">
                  {type}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Budget */}
        <div>
          <h4 className="text-sm font-semibold text-[#1A2332] mb-3">Budget</h4>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '6px',
              marginBottom: '16px',
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            {BUDGET_CHIPS.map(chip => {
              const isActive = filters.minPrice === chip.min && filters.maxPrice === chip.max;
              return (
                <button
                  key={chip.label}
                  onClick={() => setBudget(chip.min, chip.max)}
                  style={{
                    padding: '5px 10px',
                    borderRadius: '999px',
                    fontSize: '11px',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    transition: 'all 0.15s ease',
                    cursor: 'pointer',
                    backgroundColor: isActive ? '#F5C842' : '#ffffff',
                    color: isActive ? '#1A1208' : '#2A3442',
                    border: isActive ? '1px solid #F5C842' : '1px solid #e5e7eb',
                  }}
                >
                  {chip.label}
                </button>
              );
            })}
          </div>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-[#2A3442] mb-1 block">Min Price</label>
              <input
                type="number"
                value={filters.minPrice}
                onChange={e => onFilterChange({ ...filters, minPrice: Number(e.target.value) })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5C842] focus:border-transparent"
                placeholder="$0"
              />
            </div>
            <div>
              <label className="text-xs text-[#2A3442] mb-1 block">Max Price</label>
              <input
                type="number"
                value={filters.maxPrice === 999999 ? '' : filters.maxPrice}
                onChange={e => onFilterChange({ ...filters, maxPrice: Number(e.target.value) || 999999 })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5C842] focus:border-transparent"
                placeholder="Any"
              />
            </div>
          </div>
        </div>

        {/* Year Range */}
        <div>
          <h4 className="text-sm font-semibold text-[#1A2332] mb-3">Year</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#2A3442] mb-1 block">From</label>
              <input
                type="number"
                value={filters.minYear}
                onChange={e => onFilterChange({ ...filters, minYear: Number(e.target.value) })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5C842] focus:border-transparent"
                min="1990"
                max={new Date().getFullYear()}
              />
            </div>
            <div>
              <label className="text-xs text-[#2A3442] mb-1 block">To</label>
              <input
                type="number"
                value={filters.maxYear}
                onChange={e => onFilterChange({ ...filters, maxYear: Number(e.target.value) })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5C842] focus:border-transparent"
                min="1990"
                max={new Date().getFullYear()}
              />
            </div>
          </div>
        </div>

        {/* Mileage Range */}
        <div>
          <h4 className="text-sm font-semibold text-[#1A2332] mb-3">Mileage</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#2A3442] mb-1 block">Min</label>
              <input
                type="number"
                value={filters.minMileage}
                onChange={e => onFilterChange({ ...filters, minMileage: Number(e.target.value) })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5C842] focus:border-transparent"
                placeholder="0"
              />
            </div>
            <div>
              <label className="text-xs text-[#2A3442] mb-1 block">Max</label>
              <input
                type="number"
                value={filters.maxMileage === 999999 ? '' : filters.maxMileage}
                onChange={e => onFilterChange({ ...filters, maxMileage: Number(e.target.value) || 999999 })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5C842] focus:border-transparent"
                placeholder="Any"
              />
            </div>
          </div>
        </div>

        {/* Free Delivery Link */}
        <a
          href="/oregon-coast-delivery"
          className="flex items-center gap-2 px-4 py-3 bg-white rounded-lg border border-gray-200 hover:border-[#F5C842] hover:bg-[#FFFBF0] transition-all group"
        >
          <i className="ri-truck-line text-xl text-[#F5C842]"></i>
          <div className="flex-1">
            <div className="text-sm font-semibold text-[#1A2332] group-hover:text-[#F5C842] transition-colors">
              Free Delivery Available
            </div>
            <div className="text-xs text-[#2A3442]">Oregon Coast & SW Washington</div>
          </div>
          <i className="ri-arrow-right-line text-[#2A3442] group-hover:text-[#F5C842] transition-colors"></i>
        </a>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <>
        <button
          onClick={() => setIsOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:border-[#F5C842] transition-colors whitespace-nowrap"
        >
          <i className="ri-filter-3-line text-lg"></i>
          <span className="text-sm font-medium">Filters</span>
          {activeFilterCount > 0 && (
            <span className="bg-[#F5C842] text-[#1A1208] text-xs font-bold px-2 py-0.5 rounded-full">
              {activeFilterCount}
            </span>
          )}
        </button>

        {isOpen && createPortal(
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 z-[9998]"
              onClick={() => setIsOpen(false)}
            />
            
            {/* Drawer */}
            <div
              ref={drawerRef}
              className="fixed inset-x-0 bottom-0 bg-white rounded-t-2xl z-[9999] max-h-[85vh] flex flex-col animate-slide-up"
              role="dialog"
              aria-modal="true"
              aria-labelledby="filter-drawer-title"
            >
              {/* Drag Handle */}
              <div className="flex justify-center pt-3 pb-2">
                <div className="w-12 h-1 bg-gray-300 rounded-full"></div>
              </div>

              {filterContent}

              {/* Bottom Action Bar */}
              <div className="border-t border-gray-200 px-6 py-4 bg-white">
                <button
                  onClick={() => setIsOpen(false)}
                  className="w-full bg-[#F5C842] text-[#1A1208] font-semibold py-3 rounded-lg hover:bg-[#E5B832] transition-colors whitespace-nowrap"
                >
                  Show Me These {vehicleCount} {vehicleCount !== 1 ? 'Cars' : 'Car'}
                </button>
              </div>
            </div>
          </>,
          document.body
        )}
      </>
    );
  }

  // Desktop sidebar
  return (
    <div className="w-full bg-white rounded-lg shadow-sm flex flex-col sticky top-24 max-h-[calc(100vh-7rem)] overflow-hidden">
      {filterContent}
    </div>
  );
}